(() => {
  const MAX = 100, TTL = 180 * 86400000;
  const valid = item => item && typeof item.productId === 'string' && typeof item.variantId === 'string' && /^[1-9]\d{0,23}$/.test(item.productId) && /^[1-9]\d{0,23}$/.test(item.variantId);
  let guestMode = false;
  let accountItems; // Document memory only; never persist a customer's private list.
  const publishAccount = items => { accountItems = items; window.dispatchEvent(new Event('wishlist:changed')); };
  const key = item => `${item.productId}:${item.variantId}`;
  const guest = storageKey => {
    try {
      const data = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (!data || data.version !== 1 || !Number.isFinite(data.updatedAt) || data.updatedAt > Date.now() || Date.now() - data.updatedAt > TTL || !Array.isArray(data.items)) return [];
      return [...new Map(data.items.filter(valid).map(item => [key(item), { productId: item.productId, variantId: item.variantId }])).values()].slice(0, MAX);
    } catch { return []; }
  };
  const persist = (storageKey, items) => {
    // Fail visibly rather than claiming a save when storage is blocked/full.
    localStorage.setItem(storageKey, JSON.stringify({ version: 1, updatedAt: Date.now(), items }));
    window.dispatchEvent(new Event('wishlist:changed'));
  };
  // Avoid races across multiple product controls and account merge operations in this document.
  let pending = Promise.resolve();
  const serialize = task => { const result = pending.then(task, task); pending = result.catch(() => {}); return result; };
  async function api(base, method, body) {
    const response = await fetch(base, { method, headers: { 'content-type': 'application/json' }, credentials: 'same-origin', signal: AbortSignal.timeout(15000), body: body ? JSON.stringify(body) : undefined });
    const result = await response.json();
    if (!response.ok) throw Object.assign(new Error(result.error), { code: result.error });
    return result;
  }
  class NativeWishlist extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.button = this.querySelector('[data-wishlist-save]');
      this.status = this.querySelector('[data-wishlist-status]');
      this.labels = JSON.parse(this.querySelector('[data-wishlist-messages]').textContent);
      this.owner = this.closest('product-info');
      this.variantId = this.dataset.variantId;
      this.account = this.dataset.customer === 'true';
      this.known = new Set(); // Never carry private account state across page loads or identities.
      const options = { signal: this.abort.signal };
      this.button.addEventListener('click', () => this.toggle(), options);
      window.addEventListener('storage', () => this.paint(), options);
      window.addEventListener('wishlist:changed', () => this.paint(), options);
      this.owner?.addEventListener('change', event => {
        if (event.target.matches('input[name=id]')) this.sync(event.target.value);
      }, options);
      if (typeof window.subscribe === 'function') {
        this.unsubscribe?.();
        this.unsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) === this.owner?.dataset.section) this.sync(data.variant?.id);
        });
      }
      this.paint(); // Local-only initialization. No API or product fetch.
    }
    disconnectedCallback() {
      this.abort?.abort(); this.unsubscribe?.();
      clearTimeout(this.statusTimer); clearTimeout(this.statusClearTimer); cancelAnimationFrame(this.statusFrame);
    }
    message(text) {
      clearTimeout(this.statusTimer); clearTimeout(this.statusClearTimer); cancelAnimationFrame(this.statusFrame);
      this.status.classList.remove('is-visible');
      this.status.textContent = text;
      if (!text || !this.classList.contains('native-wishlist--compact')) return;
      this.statusFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.status.classList.add('is-visible');
        this.statusTimer = setTimeout(() => {
          this.status.classList.remove('is-visible');
          this.statusClearTimer = setTimeout(() => { this.status.textContent = ''; }, 220);
        }, 4000);
      });
    }
    sync(id) { this.variantId = id ? String(id) : ''; this.message(''); this.paint(); }
    entry() { return { productId: this.dataset.productId, variantId: this.variantId }; }
    paint() {
      const entry = this.entry(), saved = this.account ? (accountItems ? accountItems.some(item => key(item) === key(entry)) : this.known.has(key(entry))) : guest(this.dataset.storageKey).some(item => key(item) === key(entry));
      this.saved = saved;
      this.button.disabled = !valid(entry) || !!this.busy;
      this.button.setAttribute('aria-pressed', String(saved));
      this.querySelector('[data-wishlist-label]').textContent = saved ? this.labels.remove : this.labels.save;
      this.button.setAttribute('aria-label', `${saved ? this.labels.remove : this.labels.save}${this.dataset.productTitle ? ': ' + this.dataset.productTitle : ''}`);
    }
    async toggle() {
      if (this.busy || !valid(this.entry())) return;
      const entry = this.entry(), shouldRemove = this.saved;
      this.busy = true; this.paint();
      try {
        await serialize(async () => {
          if (this.account) {
            try {
              const root = this.dataset.proxy.replace(/\/$/, ''), local = guest(this.dataset.storageKey);
              // Clear guest data only once all idempotent chunks have been acknowledged.
              for (let index = 0; index < local.length; index += 20) await api(`${root}/wishlist/merge`, 'POST', { items: local.slice(index, index + 20) });
              const result = await api(`${root}/wishlist/items`, shouldRemove ? 'DELETE' : 'POST', entry);
              this.known = new Set(result.items.map(key));
              publishAccount(result.items);
              if (local.length) persist(this.dataset.storageKey, []);
              if (this.variantId === entry.variantId) this.message(shouldRemove ? this.labels.removed : this.labels.saved);
              return;
            } catch (error) {
              if (error.code !== 'login_required') throw error;
              // New Customer Accounts may not identify a customer through the proxy.
              this.account = false; guestMode = true;
            }
          }
          const items = guest(this.dataset.storageKey), exists = items.some(item => key(item) === key(entry));
          const remove = shouldRemove && exists;
          if (!remove && !exists && items.length >= MAX) throw Object.assign(new Error('limit'), { code: 'wishlist_limit' });
          persist(this.dataset.storageKey, remove ? items.filter(item => key(item) !== key(entry)) : exists ? items : [...items, entry]);
          if (this.variantId === entry.variantId) this.message(remove ? this.labels.removed : this.labels.local);
        });
      } catch (error) {
        if (this.variantId === entry.variantId) this.message(error.code === 'wishlist_limit' ? this.labels.limit : this.labels.error);
      } finally { this.busy = false; this.paint(); }
    }
  }
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const paint = () => {
        const items = this.dataset.customer === 'true' && !guestMode ? accountItems : guest(this.dataset.storageKey);
        const count = items?.length || 0;
        this.textContent = count > 0 ? String(count) : '';
        this.hidden = count === 0;
      };
      window.addEventListener('wishlist:changed', paint, { signal: this.abort.signal });
      window.addEventListener('storage', paint, { signal: this.abort.signal });
      paint(); // Unknown account count stays hidden until an explicit interaction.
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.account = this.dataset.customer === 'true'; this.page = 0; this.generation = 0;
      this.sharedToken = new URLSearchParams(location.hash.slice(1)).get('wishlist-share');
      this.readOnly = this.sharedToken !== null;
      this.root = this.dataset.proxy.replace(/\/$/, '');
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.status = this.querySelector('[data-wishlist-page-status]');
      this.labels = JSON.parse(this.querySelector('[data-wishlist-page-messages]').textContent);
      const options = { signal: this.abort.signal };
      this.shareButton = this.querySelector('[data-wishlist-share]');
      this.shareButton.hidden = this.readOnly;
      this.shareButton.disabled = true;
      this.shareButton.addEventListener('click', () => this.share(), options);
      this.bulkButton = this.querySelector('[data-wishlist-bulk-open]');
      this.bulkDialog = this.querySelector('[data-wishlist-bulk-dialog]');
      this.bulkButton?.addEventListener('click', () => this.openBulk(), options);
      this.querySelector('[data-bulk-cancel]')?.addEventListener('click', () => this.bulkDialog.close(), options);
      this.bulkDialog?.addEventListener('cancel', event => { if (this.bulkBusy) event.preventDefault(); }, options);
      this.bulkDialog?.addEventListener('close', () => { if (!this.bulkDialog.open) { this.bulkAbort?.abort(); this.bulkButton?.focus(); } }, options);
      this.querySelector('[data-bulk-form]')?.addEventListener('submit', event => { event.preventDefault(); this.addBulk(); }, options);
      this.querySelector('[data-bulk-items]')?.addEventListener('change', event => {
        if (event.target.matches('[data-bulk-selected]')) event.target.closest('[data-bulk-item]').querySelector('[data-bulk-quantity]').disabled = !event.target.checked;
      }, options);
      this.updateViewMode();
      window.addEventListener('hashchange', () => {
        this.sharedToken = new URLSearchParams(location.hash.slice(1)).get('wishlist-share');
        this.readOnly = this.sharedToken !== null;
        this.removeDialog?.close();
        if (!this.bulkBusy) this.bulkDialog?.close();
        this.updateViewMode();
        this.shareButton.hidden = this.readOnly;
        this.querySelector('[data-wishlist-share-url]').hidden = true;
        this.load();
      }, options);
      this.querySelector('[data-wishlist-previous]').addEventListener('click', () => { this.page--; this.render(); }, options);
      this.querySelector('[data-wishlist-next]').addEventListener('click', () => { this.page++; this.render(); }, options);
      this.querySelector('[data-wishlist-retry]').addEventListener('click', () => this.load(), options);
      this.grid.addEventListener('click', event => {
        const button = event.target.closest('[data-wishlist-remove]');
        if (button && this.grid.contains(button)) this.requestRemoval(button);
      }, options);
      this.removeDialog = this.querySelector('[data-wishlist-remove-dialog]');
      this.querySelector('[data-wishlist-remove-cancel]')?.addEventListener('click', () => this.removeDialog.close(), options);
      this.querySelector('[data-wishlist-remove-confirm]')?.addEventListener('click', () => {
        const button = this.pendingRemoval;
        this.removeDialog.close();
        if (button?.isConnected && this.grid.contains(button)) this.remove(button);
      }, options);
      this.removeDialog?.addEventListener('close', () => {
        this.pendingRemoval?.focus(); this.pendingRemoval = null;
      }, options);
      window.addEventListener('storage', () => { if (!this.account) this.load(); }, options);
      this.load(); // Requests are confined to the explicitly opened wishlist page.
    }
    disconnectedCallback() { this.abort?.abort(); this.generation++; this.catalogAbort?.abort(); this.bulkAbort?.abort(); }
    async load() {
      const generation = ++this.generation;
      this.status.textContent = this.labels.loading; this.grid.setAttribute('aria-busy', 'true');
      this.querySelector('[data-wishlist-retry]').hidden = true;
      try {
        this.items = await serialize(async () => {
          if (this.readOnly) {
            const result = await api(`${this.root}/wishlist/share/read`, 'POST', { token: this.sharedToken });
            return result.items;
          }
          if (this.account) {
            const local = guest(this.dataset.storageKey);
            try {
              for (let i = 0; i < local.length; i += 20) await api(`${this.root}/wishlist/merge`, 'POST', { items: local.slice(i, i + 20) });
              const result = await api(`${this.root}/wishlist/items`, 'GET');
              if (local.length) persist(this.dataset.storageKey, []);
              publishAccount(result.items);
              return result.items;
            } catch (error) {
              if (error.code !== 'login_required') throw error;
              this.account = false; guestMode = true;
            }
          }
          return guest(this.dataset.storageKey);
        });
        if (generation !== this.generation || !this.isConnected) return;
        await this.render();
      } catch (error) {
        if (generation === this.generation) {
          this.failure();
          if (this.readOnly && ['share_unavailable','invalid_token'].includes(error.code)) {
            this.grid.replaceChildren();
            this.status.textContent = this.labels.shareUnavailable;
            this.querySelector('[data-wishlist-retry]').hidden = true;
          }
        }
      }
    }
    failure() {
      this.status.textContent = this.labels.error;
      this.grid.setAttribute('aria-busy', 'false');
      this.querySelector('[data-wishlist-retry]').hidden = false;
    }
    async render() {
      const generation = ++this.generation;
      this.catalogAbort?.abort(); this.catalogAbort = new AbortController();
      this.page = Math.max(0, Math.min(this.page, Math.ceil(this.items.length / 12) - 1));
      const entries = this.items.slice(this.page * 12, this.page * 12 + 12);
      this.grid.setAttribute('aria-busy', 'true');
      this.querySelector('[data-wishlist-previous]').disabled = true;
      this.querySelector('[data-wishlist-next]').disabled = true;
      try {
        let cards = [];
        if (entries.length) {
          const response = await fetch(`${this.root}/wishlist/catalog`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, signal: AbortSignal.any([this.catalogAbort.signal, AbortSignal.timeout(15000)]), body: JSON.stringify({ items: entries, readOnly: this.readOnly }) });
          if (!response.ok) throw new Error('catalog');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fragment = document.querySelector('[data-wishlist-catalog]');
          if (!fragment || fragment.querySelector('script') || fragment.children.length !== entries.length) throw new Error('catalog');
          cards = [...fragment.children];
        }
        if (generation !== this.generation || !this.isConnected) return;
        this.grid.replaceChildren(...cards);
        // Retain vacant card slots so asynchronously resolved data does not move the footer.
        for (let i = cards.length; i < 12; i++) {
          const placeholder = document.createElement('div');
          placeholder.className = 'native-wishlist__placeholder'; placeholder.setAttribute('aria-hidden', 'true');
          this.grid.append(placeholder);
        }
        this.status.textContent = !this.items.length ? this.labels.empty : this.readOnly ? '' : this.account ? this.labels.account : this.labels.local;
        this.shareButton.disabled = !this.items.length;
        if (this.bulkButton) this.bulkButton.disabled = !this.items.length;
        this.grid.setAttribute('aria-busy', 'false');
        this.querySelector('[data-wishlist-previous]').disabled = this.page === 0;
        this.querySelector('[data-wishlist-next]').disabled = (this.page + 1) * 12 >= this.items.length;
        this.querySelector('[data-wishlist-page-number]').textContent = this.items.length ? `${this.page + 1} / ${Math.ceil(this.items.length / 12)}` : '';
        this.querySelector('[data-wishlist-retry]').hidden = true;
      } catch { if (generation === this.generation && this.isConnected) this.failure(); }
    }
    updateViewMode() {
      const section = this.closest('.native-wishlist__page');
      const title = section?.querySelector('[data-wishlist-title]');
      if (title) title.textContent = this.readOnly ? this.labels.sharedTitle : this.labels.title;
      const notice = this.querySelector('[data-wishlist-shared-notice]');
      if (notice) notice.hidden = !this.readOnly;
      const sharing = this.querySelector('.native-wishlist__sharing');
      if (sharing) sharing.hidden = this.readOnly;
    }
    async openBulk() {
      if (!this.items?.length || !this.bulkDialog || this.bulkDialog.open) return;
      const list = this.querySelector('[data-bulk-items]'), status = this.querySelector('[data-bulk-status]');
      const submit = this.querySelector('[data-bulk-submit]');
      this.bulkAbort?.abort(); this.bulkAbort = new AbortController();
      const signal = this.bulkAbort.signal, entries = [...this.items];
      list.replaceChildren(); submit.disabled = true; status.textContent = this.labels.loading;
      this.bulkDialog.showModal();
      try {
        for (let offset = 0; offset < entries.length; offset += 12) {
          const batch = entries.slice(offset, offset + 12);
          const response = await fetch(this.root + '/wishlist/catalog', {method:'POST', credentials:'same-origin', headers:{'content-type':'application/json'}, signal:AbortSignal.any([signal,AbortSignal.timeout(15000)]), body:JSON.stringify({items:batch,bulk:true,readOnly:true})});
          if (!response.ok) throw new Error('catalog');
          const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
          const fragment = parsed.querySelector('[data-wishlist-catalog]');
          if (!fragment || fragment.querySelector('script') || fragment.children.length !== batch.length || [...fragment.children].some(row => !row.matches('[data-bulk-item]'))) throw new Error('catalog');
          if (signal.aborted || !this.isConnected) return;
          list.append(...fragment.children);
        }
        status.textContent = this.bulkLocked ? this.labels.bulkError : '';
        submit.disabled = !!this.bulkLocked || !list.querySelector('[data-bulk-selected]:checked:not(:disabled)');
      } catch { if (!signal.aborted) status.textContent = this.labels.error; }
    }
    async addBulk() {
      if (this.bulkBusy || this.bulkLocked || this.querySelector('[data-bulk-submit]').disabled) return;
      const form = this.querySelector('[data-bulk-form]'), status = this.querySelector('[data-bulk-status]');
      if (!form.reportValidity()) return;
      const items = [...this.querySelectorAll('[data-bulk-item]')].filter(row => {
        const checkbox = row.querySelector('[data-bulk-selected]'); return checkbox.checked && !checkbox.disabled;
      }).map(row => ({id:row.dataset.variantId,quantity:Number(row.querySelector('[data-bulk-quantity]').value)}));
      if (!items.length || items.some(item => !/^[1-9]\d{0,23}$/.test(item.id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1)) { status.textContent = this.labels.bulkChoose; return; }
      this.bulkBusy = true; this.querySelector('[data-bulk-submit]').disabled = true; this.querySelector('[data-bulk-cancel]').disabled = true;
      status.textContent = this.labels.loading;
      try {
        const target = new URL(this.dataset.cartAddUrl,location.origin), cart = new URL(this.dataset.cartUrl,location.origin);
        if (target.origin !== location.origin || cart.origin !== location.origin) throw new Error('cart');
        const response = await fetch(target.href,{method:'POST',credentials:'same-origin',headers:{'content-type':'application/json'},signal:AbortSignal.timeout(15000),body:JSON.stringify({items})});
        if (!response.ok) throw new Error('cart');
        location.assign(cart.href);
      } catch { this.bulkLocked = true; status.textContent = this.labels.bulkError; }
      finally { this.bulkBusy = false; this.querySelector('[data-bulk-cancel]').disabled = false; }
    }
    async share() {
      if (this.sharing || this.readOnly || !this.items?.length) return;
      this.sharing = true; this.shareButton.disabled = true;
      const field = this.querySelector('[data-wishlist-share-url]');
      try {
        const result = await api(`${this.root}/wishlist/share`, 'POST', this.account ? {} : { items: this.items });
        const url = new URL(location.href);
        url.searchParams.set('view', 'wishlist');
        url.searchParams.delete('preview_theme_id');
        url.searchParams.delete('_fd'); url.searchParams.delete('pb');
        url.hash = new URLSearchParams({ 'wishlist-share': result.token }).toString();
        field.value = url.href; field.hidden = false;
        this.querySelector('[data-wishlist-share-note]').textContent = this.labels.shareNote;
        if (navigator.share) {
          try { await navigator.share({ title: document.title, url: url.href }); return; }
          catch (error) { if (error.name === 'AbortError') return; }
        }
        try { await navigator.clipboard.writeText(url.href); this.status.textContent = this.labels.copied; }
        catch { field.focus(); field.select(); }
      } catch { this.status.textContent = this.labels.error; }
      finally { this.sharing = false; this.shareButton.disabled = !this.items?.length; }
    }
    requestRemoval(button) {
      if (this.removing || this.readOnly || this.removeDialog?.open) return;
      if (!this.removeDialog || typeof this.removeDialog.showModal !== 'function') {
        this.status.textContent = this.labels.error; return;
      }
      this.pendingRemoval = button;
      const card = button.closest('[data-product-id]');
      this.querySelector('[data-wishlist-remove-product]').textContent = card?.querySelector('.product-item__title')?.textContent || button.getAttribute('aria-label') || '';
      this.removeDialog.showModal();
    }
    async remove(button) {
      if (this.removing || this.readOnly) return;
      const card = button.closest('[data-product-id]'), entry = { productId: card.dataset.productId, variantId: card.dataset.variantId };
      if (!valid(entry)) return;
      this.removing = true; button.disabled = true;
      try {
        await serialize(async () => {
          if (this.account) {
            const result = await api(`${this.root}/wishlist/items`, 'DELETE', entry);
            this.items = result.items; publishAccount(result.items);
          } else {
            this.items = guest(this.dataset.storageKey).filter(item => key(item) !== key(entry));
            persist(this.dataset.storageKey, this.items);
          }
        });
        await this.render();
        (this.grid.querySelector('[data-wishlist-remove]') || this.status).focus();
      } catch { this.failure(); }
      finally { this.removing = false; button.disabled = false; }
    }
  }
  if (!customElements.get('native-wishlist-count')) customElements.define('native-wishlist-count', WishlistCount);
  if (!customElements.get('native-wishlist-page')) customElements.define('native-wishlist-page', WishlistPage);
  if (!customElements.get('native-wishlist')) customElements.define('native-wishlist', NativeWishlist);
})();
